/**
 * Shares a resource with colleagues in the same account (AI-734).
 *
 * One picker over people and teams. Access is read-only in v1, so it is stated once rather than
 * offered as a one-option dropdown on every row. Additions are staged and applied together, because
 * that is the moment the user is agreeing to; removals apply immediately, because a mistake there
 * should be undone at once rather than on a later Save.
 */

import { useCallback, useMemo, useState } from "react"
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItem, ListItemAvatar,
  ListItemText, TextField, Tooltip, Typography, buttonClasses, styled,
} from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { Group, Link as LinkIcon, Person, Search } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import { useCopyToClipboard } from "hooks/use-copy-to-clipboard"
import { useNotifications } from "hooks/notifications"
import { useUserId } from "resources/account"
import {
  type DirectoryEntry,
  type ShareableResourceType,
  type ShareRecipient,
  useGrantShareAccess,
  useRevokeShareAccess,
  useShareDirectory,
  useShareRecipients,
} from "./resource-sharing"
import { ConfirmActionDialog } from "./confirm-action-dialog"

const LIST_SX = { maxHeight: "14rem", overflowY: "auto" } satisfies SxProps<Theme>
const FOOTER_SX = { justifyContent: "space-between" } satisfies SxProps<Theme>
const ACTIONS_GROUP_SX = { display: "flex", gap: 1 } satisfies SxProps<Theme>

/** A team is a rounded square, a person a circle — the shape says which before the glyph does. */
const RecipientAvatar = styled(Avatar, {
  shouldForwardProp: prop => prop !== "isTeam",
})<{ isTeam: boolean }>(({ isTeam }) => ({
  width: 30,
  height: 30,
  fontSize: "1rem",
  ...(isTeam && { borderRadius: "6px" }),
}))

/** The trailing lane of a recipient row. flex:none so a long name ellipsizes instead of squeezing the level out. */
const RowTrailing = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flex: "none",
}))
const CAPTION_SX = { display: "block", mt: 1 } satisfies SxProps<Theme>
const TITLE_ROW_SX = { display: "flex", alignItems: "flex-start", gap: 1 } satisfies SxProps<Theme>
const TITLE_TEXT_SX = { flex: 1, minWidth: 0 } satisfies SxProps<Theme>
// MUI drops DialogContent's top padding when it follows a DialogTitle, which clipped the picker's
// focused label against the scroll container.
const CONTENT_SX = { pt: 1 } satisfies SxProps<Theme>
const SECTION_LABEL_SX = { display: "block", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 } satisfies SxProps<Theme>
// Portalled, so the group headings cannot be reached from the Autocomplete's own sx. No font-size here:
// this app's root is 12px, so a rem value picked off a 16px-root design lands a quarter smaller.
const PICKER_PAPER_SX = {
  "& .MuiAutocomplete-groupLabel": { textTransform: "uppercase", letterSpacing: "0.08em" },
} satisfies SxProps<Theme>
/**
 * Both tints are per-scheme: primary-ink darkens for light and lightens for dark (plain `primary` is 2.6:1
 * on white), and primary-tint is 0.2 alpha of the light primary against 0.08 of the dark one — where the
 * theme's own text-button hover is one hard-coded wash for both.
 *
 * The colour is keyed on `&:not(.MuiButton-contained)` because that is the selector the app theme uses to
 * force `color: inherit` on every non-contained Button. Two classes beat one, so a plain `color` here loses
 * — from `styled` exactly as it did from `sx`. Measured both ways.
 */
const CopyLinkButton = styled(Button)({
  [`&:not(.${buttonClasses.contained})`]: { color: "var(--app-primary-ink)" },
  "&:hover": { backgroundColor: "var(--app-primary-tint)" },
})

export interface ShareTarget {
  resourceId: string | null
  isLoading: boolean
  isError: boolean
  isCreating: boolean
  resolveResourceId: () => Promise<string>
}

export interface ShareDialogWording {
  title: string
  accessNote: string
  shareFailure: string
}

export interface ShareDialogCopyLink {
  url: (resourceId: string) => string
  failure: string
  onCopied: (resourceId: string) => void
}

interface ShareDialogProps {
  open: boolean
  onClose: () => void
  resourceType: ShareableResourceType
  resourceName: string
  target: ShareTarget
  wording: ShareDialogWording
  idPrefix: string
  allowAdding?: boolean
  confirmation?: (recipients: DirectoryEntry[]) => { title: string, body: string }
  copyLink?: ShareDialogCopyLink
  onShared: (resourceId: string, granted: DirectoryEntry[]) => void
  onUnshared: (resourceId: string, recipient: ShareRecipient) => void
}

function entryKey({ kind, id }: DirectoryEntry): string {
  return `${kind}:${id}`
}

function recipientKinds(recipients: DirectoryEntry[]): "member" | "team" | "mixed" {
  const kinds = new Set(recipients.map(recipient => recipient.kind))
  return kinds.size > 1 ? "mixed" : (recipients[0]?.kind ?? "member")
}

export function recipientAnalyticsFields(recipients: DirectoryEntry[]) {
  return {
    recipientCount: recipients.length,
    recipientKind: recipientKinds(recipients),
    recipientUserIds: recipients.filter(entry => entry.kind === "member").map(entry => entry.id),
    recipientTeamIds: recipients.filter(entry => entry.kind === "team").map(entry => entry.id),
  }
}

/** First letter of each of the first two words. A name that yields none (blank, or all punctuation) has no initials. */
function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(word => word[0] ?? "").join("").toUpperCase()
}

function RecipientIcon({ kind, name }: { kind: DirectoryEntry["kind"], name: string }) {
  if (kind === "team") return <RecipientAvatar isTeam><Group fontSize="small" /></RecipientAvatar>
  const label = initials(name)
  return <RecipientAvatar isTeam={false}>{label === "" ? <Person fontSize="small" /> : label}</RecipientAvatar>
}

export function ShareDialog({
  open, onClose, resourceType, resourceName, target, wording, idPrefix, allowAdding = true, confirmation, copyLink, onShared, onUnshared,
}: ShareDialogProps) {
  const userId = useUserId()
  const notifications = useNotifications()
  const { copy } = useCopyToClipboard()

  const [staged, setStaged] = useState<DirectoryEntry[]>([])
  const [failure, setFailure] = useState<string | null>(null)
  const [confirmState, setConfirmState] = useState<{ title: string, body: string } | null>(null)

  const { recipients, isLoading: isLoadingRecipients, isError: isRecipientsErrored } = useShareRecipients(target.resourceId, resourceType)
  const { directory, isError: isDirectoryErrored } = useShareDirectory(open && allowAdding)

  const grantAccess = useGrantShareAccess(resourceType)
  const revokeAccess = useRevokeShareAccess(resourceType)

  const alreadyHasAccess = useMemo(
    () => new Set(recipients.map(entryKey)),
    [recipients],
  )
  // Self is excluded, not just filtered by `recipients`: before the lazy create there are no
  // recipients yet, so the sharer would still be offered — and granting themselves READ overwrites
  // the OWNER row the create just wrote. The owner row is hidden for the same reason.
  const options = useMemo(
    () => directory.filter(entry => (
      !alreadyHasAccess.has(entryKey(entry)) && !(entry.kind === "member" && entry.id === userId)
    )),
    [directory, alreadyHasAccess, userId],
  )

  const { resolveResourceId } = target

  const share = useCallback(async () => {
    if (staged.length === 0) return
    setFailure(null)
    try {
      const resourceId = await resolveResourceId()
      // One updatePermissions per recipient, aliased into one request, so some can be refused while
      // others land. Report only the ones that actually got access, and leave the rest staged.
      const refused = await grantAccess.mutateAsync({ resourceId, recipients: staged })
      const granted = staged.filter(entry => !refused.includes(entry))
      if (granted.length > 0) {
        onShared(resourceId, granted)
      }
      setStaged(refused)
      if (refused.length > 0) {
        setFailure(`Couldn't share with ${refused.map(entry => entry.name).join(", ")}. Try again.`)
      }
    } catch {
      setFailure(wording.shareFailure)
    }
  }, [staged, resolveResourceId, grantAccess, onShared, wording.shareFailure])

  const onShareClick = useCallback(() => {
    if (confirmation) {
      setConfirmState(confirmation(staged))
      return
    }
    void share()
  }, [staged, confirmation, share])

  const onConfirmShare = useCallback(() => {
    setConfirmState(null)
    void share()
  }, [share])

  const onRevoke = useCallback(async (recipient: ShareRecipient) => {
    if (target.resourceId == null) return
    setFailure(null)
    try {
      await revokeAccess.mutateAsync({ resourceId: target.resourceId, recipient })
      onUnshared(target.resourceId, recipient)
    } catch {
      setFailure("Couldn't remove access. Try again.")
    }
  }, [target.resourceId, revokeAccess, onUnshared])

  const onCopyLink = useCallback(async () => {
    if (copyLink == null) return
    setFailure(null)
    try {
      const resourceId = await resolveResourceId()
      // copy() resolves false rather than throwing when the clipboard write is refused — usually a
      // lost user-activation window after the resolveResourceId round trip. Say so either way.
      if (!await copy(copyLink.url(resourceId))) {
        setFailure("Couldn't copy the link. Try again.")
        return
      }
      notifications.success("Link copied to clipboard")
      copyLink.onCopied(resourceId)
    } catch {
      setFailure(copyLink.failure)
    }
  }, [copyLink, resolveResourceId, copy, notifications])

  const isBusy = target.isCreating || grantAccess.isPending || revokeAccess.isPending
  const shareLabel = staged.length === 0
    ? "Share"
    : `Share with ${staged.length} ${staged.length === 1 ? "recipient" : "recipients"}`

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={`${idPrefix}-title`}>
      <DialogTitle id={`${idPrefix}-title`} component="div" sx={TITLE_ROW_SX}>
        <Box sx={TITLE_TEXT_SX}>
          <Typography variant="h6" component="h2">{wording.title}</Typography>
          <Typography variant="body2" color="textSecondary" noWrap title={resourceName}>{resourceName}</Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" aria-label="Close" onClick={onClose} data-testid={`${idPrefix}-close`}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={CONTENT_SX}>
        {allowAdding && (
          <>
            <Autocomplete
              multiple
              autoHighlight
              disableCloseOnSelect
              // A pick added mid-request would be wiped by the staged reset when that request settles.
              disabled={isBusy}
              noOptionsText={isDirectoryErrored ? "Couldn't load people and teams." : "No matches"}
              options={options}
              value={staged}
              onChange={(_event, next) => setStaged(next)}
              getOptionLabel={entry => entry.name}
              isOptionEqualToValue={(a, b) => entryKey(a) === entryKey(b)}
              groupBy={entry => (entry.kind === "team" ? "Teams" : "People")}
              slotProps={{ paper: { sx: PICKER_PAPER_SX } }}
              renderOption={(props, entry) => (
                // The key has to be set here rather than spread: two directories can carry the same id
                // across kinds, and MUI's default key is the option label.
                <li {...props} key={entryKey(entry)}>
                  <ListItemText primary={entry.name} secondary={entry.email} />
                </li>
              )}
              renderValue={(value, getItemProps) => value.map((entry, index) => (
                <Chip {...getItemProps({ index })} key={entryKey(entry)} size="small" label={entry.name} icon={entry.kind === "team" ? <Group /> : <Person />} />
              ))}
              renderInput={params => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder="Add people or teams…"
                  data-testid={`${idPrefix}-picker`}
                  slotProps={{
                    ...params.slotProps,
                    // No floating label: the field is labelled here instead, which is also what stops a
                    // shrunk label being clipped by the dialog's scroll container.
                    htmlInput: { ...params.slotProps.htmlInput, "aria-label": "Add people or teams" },
                    input: {
                      ...params.slotProps.input,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                          {params.slotProps.input.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <Typography variant="caption" color="textSecondary" sx={CAPTION_SX}>
              {wording.accessNote}
            </Typography>
          </>
        )}

        {failure != null && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFailure(null)}>{failure}</Alert>}

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="textSecondary" sx={SECTION_LABEL_SX}>Who has access</Typography>
          {target.isLoading || isLoadingRecipients
            ? <CircularProgress size={20} aria-label="Loading who has access" />
            // A failed read renders identically to "nothing shared yet" unless it says otherwise,
            // and telling someone their artifact is private when it may not be is the wrong error.
            : target.isError || isRecipientsErrored
              ? <Typography variant="body2" color="error" data-testid={`${idPrefix}-list-error`}>Couldn&apos;t load who has access. Close and reopen to try again.</Typography>
            : recipients.length === 0
              ? <Typography variant="body2" color="textSecondary" data-testid={`${idPrefix}-empty`}>{allowAdding ? "Only you. Add someone above to share it." : "No one else has access."}</Typography>
              : (
                <List dense disablePadding sx={LIST_SX} data-testid={`${idPrefix}-recipients`}>
                  {recipients.map((recipient, index) => (
                    // The level is a trailing flex child rather than `secondaryAction`, which reserves a
                    // fixed 48px lane the level plus its Remove does not fit into.
                    <ListItem key={entryKey(recipient)} disableGutters divider={index < recipients.length - 1}>
                      <ListItemAvatar><RecipientIcon kind={recipient.kind} name={recipient.name} /></ListItemAvatar>
                      <ListItemText primary={recipient.name} secondary={recipient.email ?? (recipient.kind === "team" ? "Team" : undefined)} />
                      {recipient.isOwner
                        ? <Typography variant="caption" color="textSecondary">Owner</Typography>
                        : (
                          <RowTrailing>
                            {/* READ is the only level v1 grants, so it is stated per row as text — a dropdown
                                with one option is a control that decides nothing. */}
                            <Typography variant="caption" color="textSecondary">Can view</Typography>
                            <Tooltip title={`Remove ${recipient.name}`}>
                              <IconButton
                                size="small"
                                aria-label={`Remove ${recipient.name}`}
                                disabled={isBusy}
                                onClick={() => { void onRevoke(recipient) }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </RowTrailing>
                        )}
                    </ListItem>
                  ))}
                </List>
              )}
        </Box>
      </DialogContent>
      <DialogActions sx={copyLink != null ? FOOTER_SX : undefined}>
        {copyLink != null && (
          <CopyLinkButton startIcon={<LinkIcon />} disabled={isBusy} onClick={() => { void onCopyLink() }} data-testid={`${idPrefix}-copy-link`}>
            Copy link
          </CopyLinkButton>
        )}
        <Box sx={ACTIONS_GROUP_SX}>
          <Button onClick={onClose}>Done</Button>
          {allowAdding && (
            <Button
              variant="contained"
              disabled={staged.length === 0 || isBusy}
              onClick={onShareClick}
              data-testid={`${idPrefix}-confirm`}
            >
              {shareLabel}
            </Button>
          )}
        </Box>
      </DialogActions>
      {confirmation && (
        <ConfirmActionDialog
          open={confirmState != null}
          title={confirmState?.title ?? ""}
          body={confirmState?.body ?? ""}
          confirmLabel="Share"
          onClose={() => setConfirmState(null)}
          onConfirm={onConfirmShare}
        />
      )}
    </Dialog>
  )
}
